import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  type ActionFunction,
  type LoaderFunction,
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  useSubmit,
} from "react-router-dom";

import { type PutRequest } from "api";
import {
  getPlatformUserMemberships,
  putPlatformUserMembershipsByPlatformUserMembershipIdCancelMembership,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { readFormData } from "toolbox/ReadFormData";

import { invalidateMembershipQueries } from "./membershipHelper";

const load = async (platformUserMembershipId: string) => {
  const userMemberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });

  return waitObj({
    platformUserMembershipId,
    userMemberships,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipCancelConfirmLoader: LoaderFunction = ({
  params: { platformUserMembershipId },
}) => {
  if (!platformUserMembershipId) {
    return redirect("..");
  }

  return load(platformUserMembershipId);
};

export const walletMembershipCancelAction: ActionFunction = async ({ request }) => {
  const data =
    await readFormData<
      PutRequest<"/platform-user-memberships/{platformUserMembershipId}/cancel-membership">
    >(request);

  await putPlatformUserMembershipsByPlatformUserMembershipIdCancelMembership(data);

  await invalidateMembershipQueries(queryClient);

  return redirect("/wallet/memberships/manage");
};

export const WalletMembershipCancelConfirmPage = () => {
  const { t } = useTranslation();
  const location = useLocation();

  const { platformUserMembershipId, userMemberships: initialUserMemberships } =
    useLoaderData() as LoaderData;

  const navigate = useNavigate();
  const submit = useSubmit();

  const { data: userMemberships } = useQuery({
    ...getPlatformUserMemberships.keyed(),
    initialData: initialUserMemberships,
  });
  const membership = userMemberships.find(
    (m) => m.platformUserMembershipId === platformUserMembershipId,
  );

  const membershipCancellationReasonId: string = location.state?.membershipCancellationReasonId;

  if (!membershipCancellationReasonId) {
    navigate(`/wallet/memberships/manage/${platformUserMembershipId}`);
  }

  return (
    <div className="relative flex h-full flex-col bg-white">
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletMembershipCancelConfirmPage.header")}
        </div>
      </TopNav>
      <div className="flex flex-col gap-y-7 bg-white px-7 pt-3 text-left">
        <div className="text-title">
          {t("walletMembershipCancelConfirmPage.cancelScheduledTitle", {
            membershipName: membership?.membership.name,
            expirationDate: dayjs(membership?.expiration).format("MMM D, YYYY"),
          })}
        </div>
        <div className="flex flex-col gap-y-3">
          <div>{t("walletMembershipCancelConfirmPage.continuedBenefitsNotice")}</div>
          <div>
            {t("walletMembershipCancelConfirmPage.undoCancellationNotice", {
              expirationDate: dayjs(membership?.expiration).format("MMM D, YYYY"),
            })}
          </div>
        </div>
      </div>
      <div className="grow" />
      <div className="flex flex-col gap-y-3 px-7 pb-5">
        <Button
          fullWidth
          style="primary"
          onClick={async () => {
            submit(
              {
                platformUserMembershipId,
                membershipCancellationReasonId,
              },
              { method: "put" },
            );
          }}
        >
          {t("common.continue")}
        </Button>
        <Button fullWidth style="secondary" onClick={() => navigate("/wallet/memberships")}>
          {t("walletMembershipCancelConfirmPage.keepMembershipButton")}
        </Button>
      </div>
    </div>
  );
};
